import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Bot, Edit2, Trash2, Play, Pause, MessageSquare,
  BarChart3, Clock, TrendingUp, CheckCircle, AlertCircle,
  Settings, Save, X, QrCode, RefreshCw, Smartphone
} from 'lucide-react';
import agentService from '../../api/services/agentService';
import WhatsAppQRConnect from '../../components/whatsapp/WhatsAppQRConnect';

// La colonne `connections` peut revenir en JSON string (Postgres) ou en objet.
const parseConnections = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
};

const inputStyle = {
  width: '100%',
  background: 'var(--c-primary-dark)',
  color: 'var(--c-ink)',
  border: '0.5px solid var(--c-border)',
  borderRadius: 'var(--radius-md)',
  padding: '8px 12px',
  fontSize: '13px',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 400,
  letterSpacing: '0.04em',
  color: 'var(--c-text-muted)',
  marginBottom: '6px',
};

export default function AgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [agent, setAgent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [editForm, setEditForm] = useState({
    name: '', description: '', model: '', prompt: '',
    temperature: 0.7, maxTokens: 2000,
  });

  const [whatsapp, setWhatsapp] = useState({ status: null, sessionId: null, phoneNumber: null });
  const [showQR, setShowQR] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');

  useEffect(() => { loadAgentData(); }, [id]); // eslint-disable-line

  const loadAgentData = async () => {
    try {
      setLoading(true);
      setError(null);
      const agentResponse = await agentService.getAgentById(id);
      if (agentResponse.success && agentResponse.data) {
        setAgent(agentResponse.data);
        setEditForm({
          name:        agentResponse.data.name || '',
          description: agentResponse.data.description || '',
          model:       agentResponse.data.model || 'gpt-4-turbo-preview',
          prompt:      agentResponse.data.prompt || '',
          temperature: agentResponse.data.temperature || 0.7,
          maxTokens:   agentResponse.data.maxTokens || 2000,
        });
        const connections = parseConnections(agentResponse.data.connections);
        setWhatsapp({
          status:      connections?.whatsapp?.status || null,
          sessionId:   connections?.whatsapp?.sessionId || null,
          phoneNumber: connections?.whatsapp?.phoneNumber || null,
        });
      }
      try {
        const statsResponse = await agentService.getAgentStats(id);
        if (statsResponse.success && statsResponse.data) setStats(statsResponse.data);
      } catch {}
      setLoading(false);
    } catch (err) {
      setError(err.message || "Erreur lors du chargement de l'agent");
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!agent) return;
    try {
      setUpdatingStatus(true);
      const newStatus = agent.status === 'active' ? 'paused' : 'active';
      await agentService.updateAgent(id, { status: newStatus });
      await loadAgentData();
    } catch { alert('Erreur lors de la mise à jour du statut'); }
    finally { setUpdatingStatus(false); }
  };

  const handleSaveEdit = async () => {
    try {
      await agentService.updateAgent(id, editForm);
      setIsEditing(false);
      await loadAgentData();
    } catch { alert("Erreur lors de la mise à jour de l'agent"); }
  };

  const handleDelete = async () => {
    try {
      await agentService.deleteAgent(id);
      navigate('/agents');
    } catch { alert("Erreur lors de la suppression de l'agent"); }
  };

  // Lance (ou relance) une session WhatsApp : génère toujours une session neuve
  // côté WhatsFlow, ce qui fonctionne aussi bien pour une 1re connexion que pour
  // reconnecter un agent dont la session précédente a été déconnectée (logout
  // téléphone, session expirée, etc.).
  const handleConnectWhatsApp = async () => {
    try {
      setConnecting(true);
      setConnectError('');
      const response = await agentService.connectWhatsApp(id);
      const connections = parseConnections(response?.data?.connections);
      const sessionId = connections?.whatsapp?.sessionId;
      if (!sessionId) {
        setConnectError('Pas de session ID reçu du serveur.');
        return;
      }
      setWhatsapp((prev) => ({ ...prev, sessionId, status: 'connecting' }));
      setShowQR(true);
    } catch (err) {
      setConnectError(err?.message || err?.data?.message || 'Erreur lors de la connexion WhatsApp');
    } finally {
      setConnecting(false);
    }
  };

  const handleWhatsAppConnected = (phone) => {
    setShowQR(false);
    setWhatsapp((prev) => ({ ...prev, status: 'connected', phoneNumber: phone || prev.phoneNumber }));
    loadAgentData();
  };

  const tabs = [
    { id: 'overview',      label: 'Vue d\'ensemble', icon: BarChart3 },
    { id: 'config',        label: 'Configuration',   icon: Settings },
    { id: 'whatsapp',      label: 'WhatsApp',        icon: Smartphone },
    { id: 'conversations', label: 'Conversations',   icon: MessageSquare },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 mx-auto mb-4"
            style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: 'var(--c-border)', borderTopColor: 'rgb(var(--c-accent-green-rgb))' }}
          />
          <p className="text-label">Chargement de l'agent...</p>
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="card p-5" style={{ borderColor: 'rgba(224,84,84,0.3)' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={16} style={{ color: 'var(--c-error)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--c-error)' }}>
              {error || 'Agent non trouvé'}
            </p>
            <button onClick={() => navigate('/agents')} className="btn-ghost mt-3" style={{ height: '32px', fontSize: '12px' }}>
              Retour aux agents
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isActive = agent.status === 'active';

  return (
    <div>
      {/* Header page */}
      <div className="card px-5 py-3 mb-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/agents')}
            className="p-1.5 rounded-lg cursor-pointer flex-shrink-0"
            style={{ color: 'var(--c-text-gray)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--c-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            aria-label="Retour"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
          </button>

          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
          >
            <Bot size={18} strokeWidth={1.5} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
          </div>

          <div className="min-w-0">
            <h1 className="text-sm font-medium text-ink truncate">{agent.name}</h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: isActive ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-warning)' }}
              />
              <span className="text-label">{isActive ? 'Actif' : 'En pause'}</span>
              {agent.model && (
                <>
                  <span className="text-label">·</span>
                  <span className="text-label truncate">{agent.model}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleToggleStatus}
            disabled={updatingStatus}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer"
            style={{
              border: '0.5px solid',
              borderColor: isActive ? 'rgba(229,162,53,0.3)' : 'rgba(var(--c-accent-green-rgb), 0.3)',
              background:  isActive ? 'rgba(229,162,53,0.08)' : 'rgba(var(--c-accent-green-rgb), 0.08)',
              color:       isActive ? 'var(--c-warning)' : 'rgb(var(--c-accent-green-rgb))',
              opacity: updatingStatus ? 0.6 : 1,
            }}
          >
            {updatingStatus ? (
              <div className="w-3.5 h-3.5 rounded-full animate-spin" style={{ border: '1.5px solid currentColor', borderTopColor: 'transparent' }} />
            ) : isActive ? (
              <Pause size={13} strokeWidth={1.5} />
            ) : (
              <Play size={13} strokeWidth={1.5} />
            )}
            {isActive ? 'Pause' : 'Activer'}
          </button>

          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-ink cursor-pointer"
            style={{ border: '0.5px solid var(--c-border)', background: 'var(--c-hover)' }}
          >
            <Edit2 size={13} strokeWidth={1.5} />
            Modifier
          </button>

          <button
            onClick={() => setDeleteConfirm(true)}
            className="p-1.5 rounded-lg cursor-pointer"
            style={{ color: 'var(--c-text-gray)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(224,84,84,0.1)'; e.currentTarget.style.color = 'var(--c-error)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-gray)'; }}
            aria-label="Supprimer l'agent"
          >
            <Trash2 size={15} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-0 mb-5"
        style={{ borderBottom: '0.5px solid var(--c-border)' }}
      >
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isSelected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium cursor-pointer"
              style={{
                color:  isSelected ? 'rgb(var(--c-accent-green-rgb))' : 'var(--c-text-gray)',
                borderBottom: isSelected ? '2px solid rgb(var(--c-accent-green-rgb))' : '2px solid transparent',
                marginBottom: '-0.5px',
                background: 'transparent',
                border: 'none',
                borderBottom: isSelected ? '2px solid rgb(var(--c-accent-green-rgb))' : '2px solid transparent',
              }}
            >
              <Icon size={14} strokeWidth={1.5} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Vue d'ensemble */}
      {activeTab === 'overview' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: MessageSquare, label: 'Conversations', value: stats.statistics?.totalConversations || 0 },
                { icon: TrendingUp,    label: 'Messages',       value: stats.statistics?.totalMessages || 0 },
                { icon: CheckCircle,   label: 'Satisfaction',   value: `${stats.statistics?.satisfactionRate || 0}%` },
                { icon: Clock,         label: 'Temps moyen',    value: `${stats.statistics?.avgResponseTime || 0}ms` },
              ].map((s, i) => (
                <div key={i} className="card-metric p-5">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center mb-3"
                    style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
                  >
                    <s.icon size={15} strokeWidth={1.5} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                  </div>
                  <p className="text-label mb-1">{s.label}</p>
                  <p className="font-metric text-ink" style={{ fontSize: '22px', fontWeight: 600, lineHeight: 1 }}>
                    {s.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="card p-5">
            <h3 className="text-sm font-medium text-ink mb-4">Informations générales</h3>
            <div className="space-y-4">
              <div>
                <span style={labelStyle}>Description</span>
                <p className="text-sm text-ink">{agent.description || 'Aucune description'}</p>
              </div>
              <div
                className="grid grid-cols-2 gap-4 pt-4"
                style={{ borderTop: '0.5px solid var(--c-border)' }}
              >
                {[
                  { label: 'Modèle',      value: agent.model },
                  { label: 'Langue',      value: agent.language || 'fr' },
                  { label: 'Température', value: agent.temperature },
                  { label: 'Max Tokens',  value: agent.maxTokens },
                ].map((f, i) => (
                  <div key={i}>
                    <span style={labelStyle}>{f.label}</span>
                    <p className="text-sm font-medium text-ink">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Configuration */}
      {activeTab === 'config' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="card p-5"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-medium text-ink">Configuration de l'agent</h3>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="btn-ghost"
                style={{ height: '30px', padding: '0 12px', fontSize: '12px' }}
              >
                <Edit2 size={12} strokeWidth={1.5} />
                Modifier
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label style={labelStyle}>Nom</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.5)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--c-border)'; }}
                />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.5)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--c-border)'; }}
                />
              </div>
              <div>
                <label style={labelStyle}>Prompt système</label>
                <textarea
                  value={editForm.prompt}
                  onChange={e => setEditForm({ ...editForm, prompt: e.target.value })}
                  rows={8}
                  style={{ ...inputStyle, fontFamily: 'DM Mono, monospace', fontSize: '12px', resize: 'vertical' }}
                  onFocus={e => { e.target.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.5)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--c-border)'; }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label style={labelStyle}>Température</label>
                  <input
                    type="number" step="0.1" min="0" max="2"
                    value={editForm.temperature}
                    onChange={e => setEditForm({ ...editForm, temperature: parseFloat(e.target.value) })}
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.5)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--c-border)'; }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Max Tokens</label>
                  <input
                    type="number"
                    value={editForm.maxTokens}
                    onChange={e => setEditForm({ ...editForm, maxTokens: parseInt(e.target.value) })}
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = 'rgba(var(--c-accent-green-rgb), 0.5)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--c-border)'; }}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium text-ink cursor-pointer"
                  style={{ background: 'var(--c-hover)', border: '0.5px solid var(--c-border)' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn-primary flex-1"
                >
                  <Save size={13} strokeWidth={1.5} />
                  Enregistrer
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <span style={labelStyle}>Prompt système</span>
                <div
                  className="rounded-lg p-4"
                  style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                >
                  <pre
                    className="text-sm whitespace-pre-wrap text-ink"
                    style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px' }}
                  >
                    {agent.prompt || 'Aucun prompt défini'}
                  </pre>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Température', value: agent.temperature },
                  { label: 'Max Tokens',  value: agent.maxTokens },
                ].map((f, i) => (
                  <div
                    key={i}
                    className="rounded-lg p-4"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                  >
                    <span style={labelStyle}>{f.label}</span>
                    <p className="font-metric text-ink" style={{ fontSize: '18px', fontWeight: 600 }}>{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* WhatsApp */}
      {activeTab === 'whatsapp' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="card p-5"
        >
          <h3 className="text-sm font-medium text-ink mb-4">Connexion WhatsApp</h3>

          {connectError && (
            <div
              className="rounded-lg p-3 mb-4 flex items-center gap-2"
              style={{ background: 'rgba(224,84,84,0.08)', border: '0.5px solid rgba(224,84,84,0.3)' }}
            >
              <AlertCircle size={14} style={{ color: 'var(--c-error)', flexShrink: 0 }} />
              <p className="text-sm" style={{ color: 'var(--c-error)' }}>{connectError}</p>
            </div>
          )}

          {showQR ? (
            <div>
              <WhatsAppQRConnect sessionId={whatsapp.sessionId} onConnected={handleWhatsAppConnected} />
              <button
                onClick={() => { setShowQR(false); setConnectError(''); }}
                className="w-full mt-4 py-2 rounded-lg text-sm cursor-pointer"
                style={{ background: 'var(--c-hover)', border: '0.5px solid var(--c-border)', color: 'var(--c-text-gray)' }}
              >
                Annuler
              </button>
            </div>
          ) : whatsapp.status === 'connected' ? (
            <div>
              <div
                className="rounded-lg p-4 flex items-center gap-3 mb-4"
                style={{ background: 'rgba(var(--c-accent-green-rgb), 0.08)', border: '0.5px solid rgba(var(--c-accent-green-rgb), 0.3)' }}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(var(--c-accent-green-rgb), 0.15)' }}
                >
                  <CheckCircle size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink">WhatsApp connecté</p>
                  <p className="text-label">{whatsapp.phoneNumber || 'Numéro non renseigné'}</p>
                </div>
              </div>
              <button
                onClick={handleConnectWhatsApp}
                disabled={connecting}
                className="btn-ghost"
                style={{ height: '32px', padding: '0 14px', fontSize: '12px', opacity: connecting ? 0.6 : 1 }}
              >
                <RefreshCw size={13} strokeWidth={1.5} />
                {connecting ? 'Génération du QR…' : 'Reconnecter avec un autre appareil'}
              </button>
            </div>
          ) : (
            <div>
              <div
                className="rounded-lg p-4 flex items-start gap-3 mb-4"
                style={{ background: 'rgba(229,162,53,0.08)', border: '0.5px solid rgba(229,162,53,0.3)' }}
              >
                <AlertCircle size={16} style={{ color: 'var(--c-warning)', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p className="text-sm font-medium text-ink">
                    {whatsapp.status === 'disconnected' ? 'WhatsApp déconnecté' : 'Aucune session WhatsApp'}
                  </p>
                  <p className="text-label mt-1">
                    {whatsapp.status === 'disconnected'
                      ? "La session a été déconnectée (déconnexion depuis le téléphone, session expirée...). Reconnectez pour que l'agent continue à répondre."
                      : "Cet agent n'est relié à aucun numéro WhatsApp pour le moment."}
                  </p>
                </div>
              </div>
              <button
                onClick={handleConnectWhatsApp}
                disabled={connecting}
                className="btn-primary"
                style={{ opacity: connecting ? 0.6 : 1 }}
              >
                <QrCode size={14} strokeWidth={1.5} />
                {connecting ? 'Génération du QR…' : whatsapp.status === 'disconnected' ? 'Reconnecter WhatsApp' : 'Connecter WhatsApp'}
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* Conversations */}
      {activeTab === 'conversations' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="card p-5"
        >
          <h3 className="text-sm font-medium text-ink mb-4">Conversations récentes</h3>
          <div className="text-center py-12">
            <MessageSquare
              size={32}
              strokeWidth={1}
              style={{ color: 'var(--c-text-muted)', margin: '0 auto 12px' }}
            />
            <p className="text-label">Aucune conversation pour le moment</p>
          </div>
        </motion.div>
      )}

      {/* Modal suppression */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)', zIndex: 'var(--z-modal)' }}
            onClick={() => setDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="glass max-w-sm w-full rounded-xl p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(224,84,84,0.12)' }}
                >
                  <AlertCircle size={18} style={{ color: 'var(--c-error)' }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink">Supprimer l'agent ?</p>
                  <p className="text-label">Toutes les données associées seront perdues.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium text-ink cursor-pointer"
                  style={{ background: 'var(--c-hover)', border: '0.5px solid var(--c-border)' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: 'var(--c-error)' }}
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                  Supprimer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
